/**
 * @file src/helpers/analytics.ts
 * @exports analytics
 * @summary Configures Mixpanel and PostHog and provides the shared analytics methods used throughout Cognition.
 * @remarks Every event is sent to both tools while PostHog is validated as Mixpanel's replacement.
 *          PostHog is proxied through POSTHOG_URL, so ad blockers that drop Mixpanel still let it through.
 *          Mixpanel's automatic pageview is off; `Screen Viewed` is emitted by useScreenAnalytics.
 *          The voucher email goes to PostHog only, so Mixpanel receives no new personal data.
 */

import mixpanel, { type OverridedMixpanel, type Dict } from "mixpanel-browser";
import { type PostHog, posthog } from "posthog-js";
import { env } from "src/environment";
import { POSTHOG_URL } from "src/helpers/posthog";
import packageJSON from "../../package.json";

interface IdentifyArgs {
	email?: string;
	voucherId: string;
}

interface TrackOnPageExitArgs {
	eventData?: Dict;
	eventName: string;
}

class Analytics {
	mixpanel: OverridedMixpanel;
	posthog: PostHog;

	constructor() {
		this.mixpanel = mixpanel;
		this.posthog = posthog;
		if (typeof window !== "undefined") {
			this.mixpanel.init(env.MIXPANEL_TOKEN as string, {
				debug: true,
				track_pageview: false,
				disable_persistence: true,
			});
			this.posthog.init(env.POSTHOG_API_KEY, { api_host: POSTHOG_URL });
			// Register app version as a super property
			this.registerSuperProperties({ app_version: packageJSON.version });
		}
	}

	identify({ email, voucherId }: IdentifyArgs) {
		this.mixpanel.identify(voucherId);
		this.mixpanel.people.set("name", voucherId);
		this.posthog.identify(voucherId, { email, id: voucherId });
	}

	track(eventName: string, eventData?: Dict) {
		this.mixpanel.track(eventName, eventData);
		this.posthog.capture(eventName, eventData);
	}

	trackOnPageExit({ eventData, eventName }: TrackOnPageExitArgs) {
		this.mixpanel.track(eventName, eventData, { send_immediately: true, transport: "sendBeacon" });
		this.posthog.capture(eventName, eventData, { send_instantly: true, transport: "sendBeacon" });
	}

	registerSuperProperties(properties: Dict) {
		this.mixpanel.register(properties);
		this.posthog.register(properties);
	}
}

export const analytics = new Analytics();
